/**
 * Hub Global de Mapas Mentais — Experiência completa padrão MindMeister / Astral Dashboard.
 * Gerenciamento centralizado de todos os mapas mentais da plataforma com:
 * - Abas: Todos os Mapas, Favoritos e Lixeira
 * - Busca em tempo real e filtro por cliente
 * - Visualização em Grade de Cards ou Tabela Executiva
 * - Modelos de Agência, Pedro Sobral e Funis Validados
 * - Duplicação instantânea, renomeação, exclusão e restauração
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import {
  Search,
  Plus,
  LayoutTemplate,
  Star,
  Trash2,
  RotateCcw,
  Copy,
  Network,
  GitFork,
  List as ListIcon,
  Grid,
  MoreVertical,
  Edit2,
  Folder,
  Table as TableIcon,
  Share2,
} from "lucide-react";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { EmptyState, Skeleton } from "../../ui/Card";
import { AstralTemplatesModal } from "../../components/astral/AstralTemplatesModal";
import { AstralMiniPreview } from "../../components/astral/AstralMiniPreview";
import { AstralShareModal } from "../../components/astral/AstralShareModal";
import type { AstralTemplate } from "../../data/astralTemplates";

interface GlobalMapItem {
  id: string;
  clientId: string;
  clientName: string;
  clientBusinessName: string | null;
  clientWhatsapp: string | null;
  clientInstagram: string | null;
  title: string;
  status: string;
  layout: string;
  theme: string;
  data_json: string | null;
  is_favorite: boolean;
  is_archived: boolean;
  shared: boolean;
  createdAt: string;
  updatedAt: string;
  nodeCount: number;
  versionCount: number;
}

interface ClientOption {
  id: string;
  name: string;
  businessName: string | null;
}

export function MapasGlobal() {
  const navigate = useNavigate();

  const [maps, setMaps] = useState<GlobalMapItem[] | null>(null);
  const [counts, setCounts] = useState({ all: 0, favorites: 0, trash: 0 });
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "favorites" | "trash">("all");
  const [selectedClientFilter, setSelectedClientFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [loading, setLoading] = useState(true);

  // Modais
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newClientId, setNewClientId] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedTemplateForNew, setSelectedTemplateForNew] = useState<AstralTemplate | null>(null);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renamingMap, setRenamingMap] = useState<GlobalMapItem | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  const [shareMapTarget, setShareMapTarget] = useState<GlobalMapItem | null>(null);

  const [actionMenuMapId, setActionMenuMapId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  // Carrega mapas conforme aba e busca
  async function loadMaps() {
    try {
      setLoading(true);
      const res = await api.get<{ data: GlobalMapItem[]; counts: { all: number; favorites: number; trash: number } }>(
        `/maps?filter=${activeTab}&search=${encodeURIComponent(search)}`,
      );
      if (res) {
        setMaps(res.data ?? []);
        if (res.counts) setCounts(res.counts);
      }
    } catch {
      setMaps([]);
    } finally {
      setLoading(false);
    }
  }

  // Carrega clientes para o dropdown
  async function loadClients() {
    try {
      const clientsRes = await api.get<ClientOption[]>("/clients");
      setClients(clientsRes ?? []);
      if (!newClientId && clientsRes && clientsRes.length > 0) {
        setNewClientId(clientsRes[0]?.id ?? "");
      }
    } catch {
      setClients([]);
    }
  }

  useEffect(() => {
    void loadMaps();
  }, [activeTab, search]);

  useEffect(() => {
    void loadClients();
  }, []);

  // Filtro secundário por cliente selecionado
  const filteredMaps = useMemo(() => {
    if (!maps) return null;
    if (selectedClientFilter === "all") return maps;
    return maps.filter((m) => m.clientId === selectedClientFilter);
  }, [maps, selectedClientFilter]);

  // Criação de novo mapa
  async function handleCreateMap(e: FormEvent) {
    e.preventDefault();
    if (!newTitle.trim() || !newClientId) return;

    setCreating(true);
    try {
      const payload: any = {
        title: newTitle.trim(),
        clientId: newClientId,
      };

      if (selectedTemplateForNew) {
        payload.layout = selectedTemplateForNew.data.layout;
        payload.theme = selectedTemplateForNew.data.theme;
        payload.data_json = selectedTemplateForNew.data;
      }

      const res = await api.post<{ id: string }>("/maps", payload);
      setNewModalOpen(false);
      setNewTitle("");
      setSelectedTemplateForNew(null);
      navigate(`/mapas/${res.id}`);
    } catch (err: any) {
      showToast(err?.message || "Erro ao criar mapa.");
    } finally {
      setCreating(false);
    }
  }

  // Abertura do modal de modelos
  function handleSelectTemplateFromModal(template: AstralTemplate) {
    setIsTemplatesModalOpen(false);
    setSelectedTemplateForNew(template);
    setNewTitle(template.title);
    setNewModalOpen(true);
  }

  // Favoritar / Desfavoritar
  async function handleToggleFavorite(e: React.MouseEvent, mapItem: GlobalMapItem) {
    e.stopPropagation();
    try {
      const newFav = !mapItem.is_favorite;
      await api.patch(`/maps/${mapItem.id}`, { is_favorite: newFav });
      setMaps((prev) => prev ? prev.map((m) => (m.id === mapItem.id ? { ...m, is_favorite: newFav } : m)) : null);
      setCounts((prev) => ({
        ...prev,
        favorites: newFav ? prev.favorites + 1 : Math.max(0, prev.favorites - 1),
      }));
      showToast(newFav ? "Mapa adicionado aos favoritos ⭐" : "Mapa removido dos favoritos.");
    } catch {
      showToast("Erro ao alterar favorito.");
    }
  }

  // Duplicar Mapa
  async function handleDuplicateMap(e: React.MouseEvent, mapItem: GlobalMapItem) {
    e.stopPropagation();
    try {
      await api.post(`/maps/${mapItem.id}/duplicate`, {});
      showToast("Mapa duplicado com sucesso!");
      void loadMaps();
    } catch {
      showToast("Erro ao duplicar mapa.");
    }
  }

  // Renomear Mapa
  async function handleRenameSubmit(e: FormEvent) {
    e.preventDefault();
    if (!renamingMap || !renameValue.trim()) return;
    setRenaming(true);
    try {
      await api.patch(`/maps/${renamingMap.id}`, { title: renameValue.trim() });
      setRenameModalOpen(false);
      setRenamingMap(null);
      showToast("Mapa renomeado!");
      void loadMaps();
    } catch {
      showToast("Erro ao renomear.");
    } finally {
      setRenaming(false);
    }
  }

  // Excluir Mapa (ou mover para lixeira)
  async function handleDeleteMap(e: React.MouseEvent, mapItem: GlobalMapItem) {
    e.stopPropagation();
    const isTrash = mapItem.is_archived;
    const msg = isTrash
      ? "Deseja excluir permanentemente este mapa? Esta ação não pode ser desfeita."
      : "Mover este mapa para a lixeira?";

    if (!confirm(msg)) return;

    try {
      await api.delete(`/maps/${mapItem.id}`);
      showToast(isTrash ? "Mapa excluído permanentemente." : "Mapa movido para a lixeira.");
      void loadMaps();
    } catch {
      showToast("Erro ao excluir.");
    }
  }

  // Restaurar da Lixeira
  async function handleRestoreMap(e: React.MouseEvent, mapItem: GlobalMapItem) {
    e.stopPropagation();
    try {
      await api.post(`/maps/${mapItem.id}/restore`, {});
      showToast("Mapa restaurado da lixeira!");
      void loadMaps();
    } catch {
      showToast("Erro ao restaurar.");
    }
  }

  return (
    <AdminShell>
      {/* ------------------------------------------------------------- */}
      {/* HEADER PRINCIPAL DO DASHBOARD */}
      {/* ------------------------------------------------------------- */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="type-h1 text-text-cream">Mapas Mentais</h1>
            <span className="rounded-full bg-indigo-500/15 border border-indigo-500/30 px-2.5 py-0.5 text-xs font-bold text-indigo-400">
              Astral Engine
            </span>
          </div>
          <p className="type-body-sm mt-1 text-text-secondary">
            Mapeamento estratégico, organogramas operacionais e funis de alta performance padrão MindMeister.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Botão Modelos Prontos */}
          <button
            type="button"
            onClick={() => setIsTemplatesModalOpen(true)}
            className="flex items-center gap-2 rounded-[var(--radius-control)] border border-amber-500/30 bg-amber-500/10 px-3.5 py-2 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition-all shadow-sm"
          >
            <LayoutTemplate size={15} />
            <span>Usar Modelo</span>
          </button>

          {/* Botão Novo Mapa */}
          <Button
            onClick={() => {
              setSelectedTemplateForNew(null);
              setNewTitle("");
              setNewModalOpen(true);
            }}
          >
            <Plus size={15} className="mr-1.5" />
            Novo Mapa Mental
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* BARRA DE NAVEGAÇÃO POR ABAS (Todos, Favoritos, Lixeira) */}
      {/* ------------------------------------------------------------- */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-border-default pb-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-bold transition-all ${
              activeTab === "all"
                ? "bg-indigo-600 text-white shadow-md"
                : "text-text-secondary hover:bg-surface-elevated hover:text-white"
            }`}
          >
            <span>Todos os Mapas</span>
            <span className="rounded-full bg-black/20 px-1.5 py-0.5 text-[10px]">
              {counts.all}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("favorites")}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-bold transition-all ${
              activeTab === "favorites"
                ? "bg-amber-600 text-white shadow-md"
                : "text-text-secondary hover:bg-surface-elevated hover:text-white"
            }`}
          >
            <Star size={13} className={activeTab === "favorites" ? "fill-white" : ""} />
            <span>Favoritos</span>
            <span className="rounded-full bg-black/20 px-1.5 py-0.5 text-[10px]">
              {counts.favorites}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("trash")}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-bold transition-all ${
              activeTab === "trash"
                ? "bg-rose-700 text-white shadow-md"
                : "text-text-secondary hover:bg-surface-elevated hover:text-white"
            }`}
          >
            <Trash2 size={13} />
            <span>Lixeira</span>
            <span className="rounded-full bg-black/20 px-1.5 py-0.5 text-[10px]">
              {counts.trash}
            </span>
          </button>
        </div>

        {/* Controles de Busca e Visualização */}
        <div className="flex items-center gap-3">
          {/* Busca em Tempo Real */}
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              placeholder="Buscar mapa ou cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-border-default bg-surface-sunken py-1.5 pl-9 pr-3 text-xs text-text-primary placeholder:text-text-tertiary focus:border-indigo-500 focus:outline-none"
            />
          </div>

          {/* Filtro por Cliente */}
          <select
            value={selectedClientFilter}
            onChange={(e) => setSelectedClientFilter(e.target.value)}
            className="rounded-lg border border-border-default bg-surface-sunken px-2.5 py-1.5 text-xs text-text-primary focus:border-indigo-500 focus:outline-none"
          >
            <option value="all">Todos os Clientes</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Toggle Grade / Tabela */}
          <div className="flex items-center rounded-lg border border-border-default bg-surface-sunken p-0.5">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded ${viewMode === "grid" ? "bg-surface-elevated text-white" : "text-text-tertiary hover:text-white"}`}
              title="Visualização em Grade"
            >
              <Grid size={14} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded ${viewMode === "table" ? "bg-surface-elevated text-white" : "text-text-tertiary hover:text-white"}`}
              title="Visualização em Tabela"
            >
              <TableIcon size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* CONTEÚDO: GRADE OU TABELA */}
      {/* ------------------------------------------------------------- */}
      {loading ? (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-44 rounded-xl" />
          <Skeleton className="h-44 rounded-xl" />
          <Skeleton className="h-44 rounded-xl" />
        </div>
      ) : filteredMaps && filteredMaps.length === 0 ? (
        <div className="mt-12">
          <EmptyState
            title={activeTab === "trash" ? "Lixeira Vazia" : activeTab === "favorites" ? "Nenhum favorito" : "Nenhum mapa encontrado"}
            description={
              activeTab === "trash"
                ? "Nenhum mapa mental arquivado na lixeira."
                : activeTab === "favorites"
                ? "Clique na estrela de qualquer mapa para fixá-lo como favorito."
                : "Crie seu primeiro mapa mental ou escolha um modelo para começar."
            }
          />
        </div>
      ) : viewMode === "grid" ? (
        /* VISUALIZAÇÃO EM GRADE */
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMaps?.map((mapItem) => {
            const LayoutIcon = mapItem.layout === "org" ? GitFork : mapItem.layout === "list" ? ListIcon : Network;
            return (
              <div
                key={mapItem.id}
                onClick={() => navigate(`/mapas/${mapItem.id}`)}
                className="group relative flex flex-col justify-between rounded-xl border border-white/10 bg-[#111622] p-5 shadow-lg hover:border-indigo-500/50 hover:bg-[#141b2a] transition-all cursor-pointer"
              >
                <div>
                  {/* Topo do Card: Layout Badge + Estrela Favorito */}
                  <div className="flex items-center justify-between pb-3 border-b border-white/5">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-400">
                        <LayoutIcon size={13} />
                      </div>
                      <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">
                        {mapItem.layout === "org" ? "Organograma" : mapItem.layout === "list" ? "Lista" : "Mapa Radial"}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => handleToggleFavorite(e, mapItem)}
                        className={`p-1 rounded-md transition-colors ${
                          mapItem.is_favorite ? "text-amber-400" : "text-white/30 hover:text-white"
                        }`}
                        title={mapItem.is_favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                      >
                        <Star size={15} className={mapItem.is_favorite ? "fill-amber-400" : ""} />
                      </button>

                      {/* Menu de Ações */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenuMapId(actionMenuMapId === mapItem.id ? null : mapItem.id);
                          }}
                          className="p-1 text-white/40 hover:text-white rounded-md"
                        >
                          <MoreVertical size={15} />
                        </button>

                        {actionMenuMapId === mapItem.id && (
                          <div
                            className="absolute right-0 top-6 z-50 w-40 rounded-xl border border-white/10 bg-[#161c2b] p-1.5 shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setActionMenuMapId(null);
                                setRenamingMap(mapItem);
                                setRenameValue(mapItem.title);
                                setRenameModalOpen(true);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-white/80 hover:bg-white/10 hover:text-white"
                            >
                              <Edit2 size={13} />
                              Renomear
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionMenuMapId(null);
                                setShareMapTarget(mapItem);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-sky-400 hover:bg-sky-500/20"
                            >
                              <Share2 size={13} />
                              Compartilhar
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                setActionMenuMapId(null);
                                handleDuplicateMap(e, mapItem);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-white/80 hover:bg-white/10 hover:text-white"
                            >
                              <Copy size={13} />
                              Duplicar
                            </button>
                            {mapItem.is_archived ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  setActionMenuMapId(null);
                                  handleRestoreMap(e, mapItem);
                                }}
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-emerald-400 hover:bg-emerald-500/20"
                              >
                                <RotateCcw size={13} />
                                Restaurar
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={(e) => {
                                setActionMenuMapId(null);
                                handleDeleteMap(e, mapItem);
                              }}
                              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-rose-400 hover:bg-rose-500/20"
                            >
                              <Trash2 size={13} />
                              {mapItem.is_archived ? "Excluir Definitivo" : "Mover p/ Lixeira"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mini Preview Vetorial Astral */}
                  <div className="mt-3 overflow-hidden rounded-lg border border-white/5 bg-[#0d111a]">
                    <AstralMiniPreview
                      data={mapItem.data_json}
                      layout={mapItem.layout}
                      theme={mapItem.theme}
                      title={mapItem.title}
                    />
                  </div>

                  {/* Título & Cliente */}
                  <div className="mt-3">
                    <h3 className="text-sm font-bold text-white group-hover:text-indigo-400 transition-colors line-clamp-1">
                      {mapItem.title}
                    </h3>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-white/50">
                      <Folder size={12} className="text-indigo-400/80" />
                      <span className="truncate">{mapItem.clientName}</span>
                    </div>
                  </div>
                </div>

                {/* Rodapé do Card: Contagem e Data */}
                <div className="mt-5 flex items-center justify-between pt-3 border-t border-white/5 text-[11px] text-white/40">
                  <div className="flex items-center gap-3">
                    <span>{mapItem.nodeCount} tópicos</span>
                    {mapItem.shared && <span className="text-sky-400">Público</span>}
                  </div>
                  <span>{new Date(mapItem.updatedAt).toLocaleDateString("pt-BR")}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* VISUALIZAÇÃO EM TABELA */
        <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-[#111622] shadow-lg">
          <table className="w-full text-left text-xs text-white/80">
            <thead className="border-b border-white/10 bg-white/5 text-[11px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="py-3 px-4">Mapa Mental</th>
                <th className="py-3 px-4">Cliente</th>
                <th className="py-3 px-4">Estrutura</th>
                <th className="py-3 px-4">Tópicos</th>
                <th className="py-3 px-4">Atualizado</th>
                <th className="py-3 px-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredMaps?.map((mapItem) => (
                <tr
                  key={mapItem.id}
                  onClick={() => navigate(`/mapas/${mapItem.id}`)}
                  className="hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="py-3.5 px-4 font-semibold text-white">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => handleToggleFavorite(e, mapItem)}
                        className={`p-1 rounded ${mapItem.is_favorite ? "text-amber-400" : "text-white/20 hover:text-white"}`}
                      >
                        <Star size={13} className={mapItem.is_favorite ? "fill-amber-400" : ""} />
                      </button>
                      <span>{mapItem.title}</span>
                    </div>
                  </td>
                  <td className="py-3.5 px-4 text-white/70">{mapItem.clientName}</td>
                  <td className="py-3.5 px-4 capitalize text-white/50">{mapItem.layout}</td>
                  <td className="py-3.5 px-4">{mapItem.nodeCount}</td>
                  <td className="py-3.5 px-4 text-white/50">
                    {new Date(mapItem.updatedAt).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => handleDuplicateMap(e, mapItem)}
                        className="p-1 text-white/40 hover:text-white rounded"
                        title="Duplicar"
                      >
                        <Copy size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDeleteMap(e, mapItem)}
                        className="p-1 text-white/40 hover:text-rose-400 rounded"
                        title="Excluir"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: NOVO MAPA MENTAL */}
      {/* ------------------------------------------------------------- */}
      {newModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
          onClick={() => setNewModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111622] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-bold text-white">Criar Novo Mapa Mental</h2>
            <p className="mt-1 text-xs text-white/60">
              Vincule a um cliente e inicie com a estrutura padrão ou modelo selecionado.
            </p>

            <form onSubmit={handleCreateMap} className="mt-5 flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-white/80 mb-1.5">Cliente</label>
                <select
                  value={newClientId}
                  onChange={(e) => setNewClientId(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                  required
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id} className="bg-[#111622]">
                      {c.name} {c.businessName ? `(${c.businessName})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-white/80 mb-1.5">Título do Mapa</label>
                <input
                  type="text"
                  placeholder="Ex: Planejamento Estratégico Q4"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white placeholder-white/30 focus:border-indigo-500 focus:outline-none"
                  required
                  autoFocus
                />
              </div>

              {selectedTemplateForNew && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">
                  <span>Modelo ativo: <strong>{selectedTemplateForNew.title}</strong></span>
                </div>
              )}

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setNewModalOpen(false)}
                  className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white/70 hover:bg-white/10"
                >
                  Cancelar
                </button>
                <Button type="submit" loading={creating}>
                  Criar Mapa
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL: RENOMEAR MAPA */}
      {/* ------------------------------------------------------------- */}
      {renameModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
          onClick={() => setRenameModalOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111622] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-bold text-white">Renomear Mapa Mental</h2>
            <form onSubmit={handleRenameSubmit} className="mt-4 flex flex-col gap-4">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white outline-none focus:border-indigo-500"
                required
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRenameModalOpen(false)}
                  className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white/70 hover:bg-white/10"
                >
                  Cancelar
                </button>
                <Button type="submit" loading={renaming}>
                  Salvar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Modelos Astral */}
      <AstralTemplatesModal
        isOpen={isTemplatesModalOpen}
        onClose={() => setIsTemplatesModalOpen(false)}
        onSelectTemplate={handleSelectTemplateFromModal}
      />

      {/* Modal de Compartilhamento Astral */}
      {shareMapTarget && (
        <AstralShareModal
          isOpen={true}
          onClose={() => setShareMapTarget(null)}
          mapId={shareMapTarget.id}
          mapTitle={shareMapTarget.title}
          isPublic={shareMapTarget.shared}
        />
      )}

      {/* Toast Feedback */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-semibold text-white shadow-2xl backdrop-blur-md animate-fade-in">
          {toast}
        </div>
      )}
    </AdminShell>
  );
}
